// React and Next.js imports
"use client";
import * as React from "react";
import {
  ThemeProvider as NextThemesProvider,
  useTheme as useNextTheme,
} from "next-themes";

// Providers/Context
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { themes } from "@/config/theme";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: string;
};

type ThemeProviderState = {
  theme: string;
  setTheme: (theme: string) => void;
  themes: typeof themes;
};

const ThemeProviderContext = createContext<ThemeProviderState>({
  theme: "zinc",
  setTheme: () => null,
  themes: themes,
});

const ThemeWrapper = ({
  children,
  defaultTheme = "zinc",
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<string>(defaultTheme);
  const { resolvedTheme: mode } = useNextTheme();

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove previous theme classes
    root.classList.remove(...themes.map((theme) => theme.name));

    // Add current theme class
    root.classList.add(theme);

    // Apply CSS variables based on current theme and mode
    const selectedTheme = themes.find((t) => t.name === theme);
    if (selectedTheme) {
      const vars =
        mode === "dark"
          ? selectedTheme.cssVars.dark
          : selectedTheme.cssVars.light;
      Object.entries(vars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
      });
    }
  }, [theme, mode]);

  const contextValue = useMemo(
    () => ({
      theme,
      setTheme,
      themes,
    }),
    [theme, setTheme, themes]
  );

  return (
    <ThemeProviderContext.Provider value={contextValue}>
      {children}
    </ThemeProviderContext.Provider>
  );
};

export function ThemeProvider({
  children,
  defaultTheme = "zinc",
  ...props
}: Readonly<ThemeProviderProps>) {
  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      {...props}
    >
      <ThemeWrapper defaultTheme={defaultTheme}>{children}</ThemeWrapper>
    </NextThemesProvider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider");

  return context;
};
